/**
 * FERP Portal - Compliance List Page
 *
 * OPTIMIZE phase primitive - Audit/Compliance.
 * Displays audit records from backend via useListAuditsQuery.
 * Sprint 3b (DRR-125): PolicyAnalyticsDashboard wired in via second tab.
 * Sprint 3c (DRR-125): Care graph visualization via Cytoscape.js (DRR-110 typed edges).
 *   DRR-114: Perception-safe labeling on all graph labels.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Data from backend via useListAuditsQuery + inline gql (taskGroups / configEdges)
 * - Multi-tenant via useTenantId()
 * - Dumb Frontend: All data from backend, no hardcoded values
 * - Cytoscape via raw API + useRef (no react-cytoscapeJS wrapper installed)
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { useTerminology, PolicyAnalyticsDashboard } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
  Switch,
  Modal,
  Tabs,
  Select,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
  IconCheck,
  IconX,
  IconList,
  IconChartBar,
  IconNetwork,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { useTenantId } from '@/auth';
import {
  GqlComplianceStatus,
  GetConfigEdgesForCareGraphDocument,
  GetTaskGroupsForCareGraphDocument,
  TaskGroupCategory,
  type AuditFieldsFragment,
  useGetConfigEdgesForCareGraphQuery,
  useGetTaskGroupsForCareGraphQuery,
  useListAuditsQuery,
} from '@/generated/graphql';
import cytoscape from 'cytoscape';

// =============================================================================
// GraphQL — care graph queries (DRR-110 typed edges, not yet in codegen)
// =============================================================================

// =============================================================================
// Care graph edge type metadata — perception-safe labels (DRR-114)
// =============================================================================

interface EdgeMeta {
  label: string;
  color: string;
}

const EDGE_META: Record<string, EdgeMeta> = {
  MANDATED_BY: { label: 'Required by', color: '#e03131' },
  IMPROVES: { label: 'Supports outcome', color: '#2f9e44' },
  MITIGATES: { label: 'Reduces risk for', color: '#e67700' },
  REIMBURSES_VIA: { label: 'Billing pathway', color: '#1971c2' },
  REQUIRES_COMPETENCY: { label: 'Requires training', color: '#6741d9' },
  FULFILLS: { label: 'Advances goal', color: '#0c8599' },
  EVIDENCED_BY: { label: 'Documented via', color: '#5c7cfa' },
  IMPLEMENTS: { label: 'Implements', color: '#845ef7' },
};

const DEFAULT_EDGE_META: EdgeMeta = { label: 'Connected', color: '#868e96' };

function getEdgeMeta(edgeType: string): EdgeMeta {
  return EDGE_META[edgeType] ?? DEFAULT_EDGE_META;
}

// =============================================================================
// CareGraphVisualization — Cytoscape canvas rendered via useRef
// =============================================================================

interface CareGraphNode {
  id: string;
  label: string;
  nodeType: 'taskgroup' | 'ruleset' | 'tag';
  color?: string | null;
  isLifeCritical?: boolean;
}

interface CareGraphEdge {
  source: string;
  target: string;
  edgeType: string;
}

interface CareGraphVisualizationProps {
  nodes: CareGraphNode[];
  edges: CareGraphEdge[];
}

function CareGraphVisualization({ nodes, edges }: CareGraphVisualizationProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || nodes.length === 0) return;

    const elements: cytoscape.ElementDefinition[] = [
      ...nodes.map((n) => ({
        data: {
          id: n.id,
          label: n.label,
          nodeType: n.nodeType,
          nodeColor: n.color ?? '#228be6',
          isLifeCritical: n.isLifeCritical ?? false,
        },
      })),
      ...edges.map((e, i) => ({
        data: {
          id: `edge-${i}`,
          source: e.source,
          target: e.target,
          edgeType: e.edgeType,
          edgeColor: getEdgeMeta(e.edgeType).color,
          edgeLabel: getEdgeMeta(e.edgeType).label,
        },
      })),
    ];

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node[nodeType = "taskgroup"]',
          style: {
            'background-color': 'data(nodeColor)' as unknown as string,
            label: 'data(label)',
            shape: 'ellipse',
            width: 90,
            height: 90,
            'font-size': 10,
            color: '#ffffff',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'border-width': 0,
          },
        },
        {
          selector: 'node[nodeType = "taskgroup"][?isLifeCritical]',
          style: {
            'border-width': 3,
            'border-color': '#fa5252',
          },
        },
        {
          selector: 'node[nodeType = "ruleset"]',
          style: {
            'background-color': '#495057',
            label: 'data(label)',
            shape: 'polygon',
            // hexagon approximation (6-point polygon)
            'shape-polygon-points': '-0.5 -1 0.5 -1 1 0 0.5 1 -0.5 1 -1 0' as unknown as string,
            width: 80,
            height: 80,
            'font-size': 9,
            color: '#ffffff',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '70px',
          },
        },
        {
          selector: 'node[nodeType = "tag"]',
          style: {
            'background-color': '#74c0fc',
            label: 'data(label)',
            shape: 'round-rectangle',
            width: 70,
            height: 35,
            'font-size': 9,
            color: '#1c1c1c',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '65px',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': 'data(edgeColor)' as unknown as string,
            'target-arrow-color': 'data(edgeColor)' as unknown as string,
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            label: 'data(edgeLabel)',
            'font-size': 9,
            color: '#495057',
            'text-rotation': 'autorotate',
            'text-margin-y': -8,
          },
        },
        {
          selector: ':selected',
          style: {
            'border-width': 3,
            'border-color': '#f08c00',
          },
        },
      ],
      layout: {
        name: 'cose',
        animate: false,
        nodeRepulsion: () => 8000,
        idealEdgeLength: () => 120,
        padding: 20,
      },
    });

    return () => {
      cy.destroy();
    };
  }, [nodes, edges]);

  if (nodes.length === 0) {
    return (
      <Alert icon={<IconNetwork size={16} />} color="blue" variant="light">
        Care graph data loading... Select a task group category above to visualize care delivery
        relationships.
      </Alert>
    );
  }

  return (
    <Box
      ref={containerRef}
      style={{
        width: '100%',
        height: 480,
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
        background: '#f8f9fa',
      }}
    />
  );
}

// =============================================================================
// CareGraphLegend
// =============================================================================

function CareGraphLegend() {
  const visibleEdgeTypes = ['MANDATED_BY', 'IMPROVES', 'MITIGATES', 'REIMBURSES_VIA', 'FULFILLS'];
  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Text size="xs" fw={600} c="dimmed">
          Edge Types
        </Text>
        <Group gap="sm" wrap="wrap">
          {visibleEdgeTypes.map((et) => {
            const meta = getEdgeMeta(et);
            return (
              <Group key={et} gap={4}>
                <Box
                  style={{ width: 20, height: 3, background: meta.color, borderRadius: 2 }}
                />
                <Text size="xs" c="dimmed">
                  {meta.label}
                </Text>
              </Group>
            );
          })}
        </Group>
        <Group gap="sm" wrap="wrap">
          <Group gap={4}>
            <Box
              style={{ width: 14, height: 14, borderRadius: '50%', background: '#228be6' }}
            />
            <Text size="xs" c="dimmed">
              Task group
            </Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 14, height: 14, background: '#495057' }} />
            <Text size="xs" c="dimmed">
              Regulation / Ruleset
            </Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 14, height: 8, background: '#74c0fc', borderRadius: 2 }} />
            <Text size="xs" c="dimmed">
              Care outcome tag
            </Text>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}

// =============================================================================
// CareGraphTab — queries task groups + edges, builds Cytoscape data
// =============================================================================

const CATEGORY_OPTIONS = Object.entries(TaskGroupCategory).map(([key, value]) => ({
  value,
  label: key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' '),
}));

function CareGraphTab() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: taskGroupData, loading: tgLoading } = useQuery<{
    taskGroups: Array<{
      id: string;
      configId: string;
      name: string;
      category: string;
      color: string | null;
      isLifeCritical: boolean;
    }>;
  }>(GetTaskGroupsForCareGraphDocument, {
    variables: { category: selectedCategory ?? undefined },
  });

  const taskGroups = taskGroupData?.taskGroups ?? [];

  // Anchor the edge query on the first visible task group's configId.
  // Sprint 3c fetches edges for the anchor node; future iterations can
  // paginate across all visible nodes.
  const anchorConfigId = taskGroups[0]?.configId ?? '';

  const { data: edgeData, loading: edgeLoading } = useQuery<{
    configEdges: Array<{
      sourceId: string;
      targetId: string;
      edgeType: string;
      inverseType: string;
      ring: number;
    }>;
  }>(GetConfigEdgesForCareGraphDocument, {
    variables: { configId: anchorConfigId },
    skip: !anchorConfigId,
  });

  const loading = tgLoading || edgeLoading;

  const { graphNodes, graphEdges } = useMemo(() => {
    const nodesMap = new Map<string, CareGraphNode>();

    for (const tg of taskGroups.slice(0, 10)) {
      nodesMap.set(tg.configId, {
        id: tg.configId,
        label: tg.name,
        nodeType: 'taskgroup',
        color: tg.color,
        isLifeCritical: tg.isLifeCritical,
      });
    }

    const rawEdges = edgeData?.configEdges ?? [];
    const edges: CareGraphEdge[] = [];

    for (const edge of rawEdges) {
      let targetNodeType: CareGraphNode['nodeType'] = 'ruleset';
      if (edge.targetId.startsWith('tag:')) {
        targetNodeType = 'tag';
      } else if (edge.targetId.startsWith('tg-')) {
        targetNodeType = 'taskgroup';
      }

      if (!nodesMap.has(edge.targetId)) {
        const rawLabel = edge.targetId.startsWith('tag:')
          ? edge.targetId.replace(/^tag:[^:]+:/, '').replace(/-/g, ' ')
          : edge.targetId;
        nodesMap.set(edge.targetId, {
          id: edge.targetId,
          label: rawLabel,
          nodeType: targetNodeType,
        });
      }

      if (nodesMap.has(edge.sourceId)) {
        edges.push({
          source: edge.sourceId,
          target: edge.targetId,
          edgeType: edge.edgeType,
        });
      }
    }

    return { graphNodes: Array.from(nodesMap.values()), graphEdges: edges };
  }, [taskGroups, edgeData]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Text fw={600}>Care Delivery Relationship Graph</Text>
          <Text size="sm" c="dimmed">
            Visualizes how care delivery activities connect to regulatory requirements, quality
            outcomes, and reimbursement pathways (DRR-110 typed edges).
          </Text>
        </div>
        <Select
          label="Filter by category"
          placeholder="All categories"
          data={CATEGORY_OPTIONS}
          value={selectedCategory}
          onChange={setSelectedCategory}
          clearable
          w={220}
        />
      </Group>

      {loading ? (
        <Group justify="center" py="xl">
          <Stack align="center" gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading care graph data...
            </Text>
          </Stack>
        </Group>
      ) : (
        <CareGraphVisualization nodes={graphNodes} edges={graphEdges} />
      )}

      <CareGraphLegend />

      {graphNodes.length > 0 && (
        <Group gap="md">
          <Badge variant="light" color="blue">
            {graphNodes.filter((n) => n.nodeType === 'taskgroup').length} task groups
          </Badge>
          <Badge variant="light" color="gray">
            {graphNodes.filter((n) => n.nodeType === 'ruleset').length} regulations
          </Badge>
          <Badge variant="light" color="cyan">
            {graphNodes.filter((n) => n.nodeType === 'tag').length} outcome tags
          </Badge>
          <Badge variant="light" color="violet">
            {graphEdges.length} relationships
          </Badge>
        </Group>
      )}
    </Stack>
  );
}

export function ComplianceListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const [activeTab, setActiveTab] = useState<string | null>('audits');
  const [searchQuery, setSearchQuery] = useState('');
  const [complianceOnly, setComplianceOnly] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<AuditFieldsFragment | null>(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const { data, loading, error, refetch } = useListAuditsQuery({
    variables: { tenantId, limit: 100 },
    skip,
  });

  const audits = useMemo(() => {
    return data?.listAudits?.audits ?? [];
  }, [data]);

  const filteredAudits = useMemo(() => {
    let result = audits;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.auditName.toLowerCase().includes(query) ||
          a.auditCode.toLowerCase().includes(query) ||
          a.frameworkType?.toLowerCase().includes(query),
      );
    }

    if (complianceOnly) {
      result = result.filter((a) => a.complianceStatus === GqlComplianceStatus.COMPLIANT);
    }

    return result;
  }, [audits, searchQuery, complianceOnly]);

  const stats = useMemo(() => {
    const total = audits.length;
    const compliant = audits.filter(
      (a) => a.complianceStatus === GqlComplianceStatus.COMPLIANT,
    ).length;
    const violations = audits.filter(
      (a) => a.complianceStatus === GqlComplianceStatus.NON_COMPLIANT,
    ).length;
    return { total, compliant, violations };
  }, [audits]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view compliance records.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Audits">
          {error.message}
        </Alert>
      </Container>
    );
  }

  const complianceColor = (status: string) => {
    switch (status) {
      case GqlComplianceStatus.COMPLIANT:
        return 'green';
      case GqlComplianceStatus.PENDING_REVIEW:
        return 'yellow';
      case GqlComplianceStatus.NON_COMPLIANT:
        return 'red';
      case GqlComplianceStatus.EXPIRED:
        return 'orange';
      default:
        return 'gray';
    }
  };

  const handleRowClick = (audit: AuditFieldsFragment) => {
    setSelectedAudit(audit);
    openDetail();
  };

  return (
    <Container size="xl" py="xl">
      {/* Audit detail modal — rendered outside Tabs so it works regardless of active tab */}
      <Modal
        opened={detailOpened}
        onClose={closeDetail}
        title={selectedAudit?.auditName ?? 'Audit Detail'}
        size="md"
      >
        {selectedAudit && (
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Code</Text>
              <Text size="sm" fw={500}>{selectedAudit.auditCode}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Framework</Text>
              <Badge variant="light">{selectedAudit.frameworkType}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Status</Text>
              <Badge color={complianceColor(selectedAudit.complianceStatus)}>
                {selectedAudit.complianceStatus}
              </Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Actions</Text>
              <Text size="sm">{selectedAudit.actionTypes.length} types</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Created</Text>
              <Text size="sm">{new Date(selectedAudit.createdAt).toLocaleDateString()}</Text>
            </Group>
          </Stack>
        )}
      </Modal>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="audits" leftSection={<IconList size={16} />}>
            {translate('audit')} Records
          </Tabs.Tab>
          <Tabs.Tab value="policy-analytics" leftSection={<IconChartBar size={16} />}>
            Policy Analytics
          </Tabs.Tab>
          <Tabs.Tab value="care-graph" leftSection={<IconNetwork size={16} />}>
            Care Graph
          </Tabs.Tab>
        </Tabs.List>

        {/* ------------------------------------------------------------------ */}
        {/* Tab 1: Audit records (original content, unchanged logic)            */}
        {/* ------------------------------------------------------------------ */}
        <Tabs.Panel value="audits" pt="md">
          <Stack gap="lg">
            {/* Header */}
            <Group justify="space-between">
              <div>
                <Title order={2}>{translate('audit')}s</Title>
                <Text c="dimmed" size="sm">
                  {translate('audit')} records and compliance framework monitoring
                </Text>
              </div>
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                onClick={() => refetch()}
                loading={loading}
              >
                Refresh
              </Button>
            </Group>

            {/* Stats Cards */}
            <Group gap="md">
              <Card withBorder p="md" style={{ flex: 1 }}>
                <Group gap="xs">
                  <ThemeIcon variant="light" size="sm">
                    <IconShieldCheck size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Total Audits</Text>
                    <Text size="xl" fw={700}>{stats.total}</Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="md" style={{ flex: 1 }}>
                <Group gap="xs">
                  <ThemeIcon variant="light" size="sm" color="green">
                    <IconCheck size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Compliant</Text>
                    <Text size="xl" fw={700}>{stats.compliant}</Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="md" style={{ flex: 1 }}>
                <Group gap="xs">
                  <ThemeIcon variant="light" size="sm" color="red">
                    <IconX size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Violations</Text>
                    <Text size="xl" fw={700}>{stats.violations}</Text>
                  </div>
                </Group>
              </Card>
            </Group>

            {/* Filters */}
            <Group gap="md">
              <TextInput
                placeholder="Search audits..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Switch
                label="Compliant only"
                checked={complianceOnly}
                onChange={(e) => setComplianceOnly(e.currentTarget.checked)}
              />
            </Group>

            {/* Audits Table */}
            {loading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Audit</Table.Th>
                      <Table.Th>Framework</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                      <Table.Th>Created</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredAudits.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text ta="center" c="dimmed" py="md">
                            No audit records found
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      filteredAudits.slice(0, 50).map((audit) => (
                        <Table.Tr
                          key={audit.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(audit)}
                        >
                          <Table.Td>
                            <Stack gap={0}>
                              <Text size="sm" fw={500} lineClamp={1}>{audit.auditName}</Text>
                              <Text size="xs" c="dimmed">{audit.auditCode}</Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" size="sm">{audit.frameworkType}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={complianceColor(audit.complianceStatus)} size="sm">
                              {audit.complianceStatus}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">{audit.actionTypes.length} types</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {new Date(audit.createdAt).toLocaleDateString()}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

            <Badge variant="light" color="cyan" style={{ alignSelf: 'flex-start' }}>
              OPTIMIZE Phase
            </Badge>
          </Stack>
        </Tabs.Panel>

        {/* ------------------------------------------------------------------ */}
        {/* Tab 2: Policy Analytics — cross-policy summary view                */}
        {/* ------------------------------------------------------------------ */}
        <Tabs.Panel value="policy-analytics" pt="md">
          <PolicyAnalyticsDashboard showSummary />
        </Tabs.Panel>

        {/* ------------------------------------------------------------------ */}
        {/* Tab 3: Care Graph — DRR-110 typed edge visualization (Sprint 3c)   */}
        {/* ------------------------------------------------------------------ */}
        <Tabs.Panel value="care-graph" pt="md">
          <CareGraphTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default ComplianceListPage;
